import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { fromEvent, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { environment } from '@env/environment';

import { ConfirmationModalComponent } from '../../routes/components/modals/confirmation-modal/confirmation-modal.component';
import { LogService } from '../../services/logging/log.service';
import { BrowserStorageService } from '@shared/services/browser-storage.service';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckerService implements OnDestroy {
  protected readonly STORAGE_KEY = 'current_version';
  protected readonly CURRENT_VERSION = '__BUILD_VERSION__'; // This will be replaced during build
  private stop$ = new Subject<void>();

  constructor(
    private browserStorage: BrowserStorageService,
    private dialog: MatDialog,
    private logger: LogService
  ) {
    this.initCheck();
  }

  protected initCheck() {
    const currentVersion = this.getCurrentVersion();
    console.info(
      'App version:',
      currentVersion === '__BUILD_VERSION__' ? 'Development' : currentVersion
    );
    this.browserStorage.put(this.STORAGE_KEY, currentVersion);
  }

  isCheckingAvailable() {
    return environment.production;
  }

  runChecking() {
    if (this.isCheckingAvailable()) {
      // Check version only when page becomes visible (user switches back to tab)
      fromEvent(document, 'visibilitychange')
        .pipe(
          takeUntil(this.stop$),
          filter(() => !document.hidden) // Only when page becomes visible
        )
        .subscribe(() => {
          setTimeout(() => this.checkVersion(), 1000); // Small delay to avoid immediate checks
        });
    }
  }

  checkVersion() {
    const currentVersion = this.getCurrentVersion();
    if (!this.versionIsCorrect(currentVersion)) {
      this.stopChecking();
      this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            confirmLabel: 'Refresh',
            title: 'New version arrived!',
            text: 'A new version of the application has been detected. Click \'Refresh\' to update the page or \'Cancel\' to continue using the previous version.',
          },
        })
        .componentInstance.actionCallback.subscribe(() => {
          this.reloadApp();
        });
    }
  }

  protected versionIsCorrect(newVersion: string) {
    return this.browserStorage.get<string>(this.STORAGE_KEY) === newVersion;
  }

  getCurrentVersion(): string {
    // In development, this will be '__BUILD_VERSION__'
    // In production, this will be replaced with actual version during build
    return this.CURRENT_VERSION;
  }

  stopChecking() {
    this.stop$.next();
    this.stop$.complete();
  }

  reloadApp() {
    this.logger.info('App reloaded by version checker');
    let url = window.location.href;
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    url += separator + 'nocache=' + Date.now();
    window.location.href = url;
  }

  ngOnDestroy() {
    this.stopChecking();
  }
}
